import { supabase } from "@/integrations/supabase/client";
import {
  fetchScopedDocuments,
  type ScopedDocumentIndex,
} from "@/domains/documents/history/documentHistoryScopedDocuments";
import {
  actorFromProfile,
  DOCUMENT_HISTORY_SOURCE_BATCH_SIZE,
  resolveSnapshotAt,
  resolveWatermark,
  type DocumentHistoryEntry,
  type DocumentHistoryChange,
  type DocumentHistoryChangeField,
  type DocumentHistoryScope,
  type DocumentHistorySourceCursor,
  type HistoryProfileRow,
} from "@/domains/documents/history/documentHistoryShared";

interface AuditEventRow {
  id: number;
  entity_id: string;
  event_type:
    | "document.deleted"
    | "document.restored"
    | "document.category.updated"
    | "document.discipline.updated"
    | "document.stage.updated"
    | "document.renamed";
  field_name: string | null;
  previous_value: unknown;
  new_value: unknown;
  created_at: string;
  user_id: number;
  actor: HistoryProfileRow | null;
}

function exhausted(cursor: DocumentHistorySourceCursor): DocumentHistorySourceCursor {
  return {
    offset: cursor.offset,
    done: true,
    snapshotAt: cursor.snapshotAt,
    watermark: cursor.watermark,
    buffered: [],
  };
}

const AUDIT_EVENT_TYPES: AuditEventRow["event_type"][] = [
  "document.deleted",
  "document.restored",
  "document.category.updated",
  "document.discipline.updated",
  "document.stage.updated",
  "document.renamed",
];

function historyAction(eventType: AuditEventRow["event_type"]): DocumentHistoryEntry["action"] {
  if (eventType === "document.deleted") return "deleted";
  if (eventType === "document.restored") return "restored";
  if (eventType === "document.renamed") return "renamed";
  return "edited";
}

const EDIT_FIELDS: Record<string, DocumentHistoryChangeField> = {
  categories: "categories",
  disciplines: "disciplines",
  stages: "stages",
};

function scalarChangeValue(value: unknown): string[] | null {
  if (typeof value !== "string") return null;
  const label = value.trim();
  return label ? [label] : [];
}

function listItemLabel(item: unknown): string | null {
  if (typeof item === "string") return item.trim();
  if (typeof item !== "object" || item === null || !("name" in item)) return null;
  return typeof item.name === "string" ? item.name.trim() : null;
}

function listChangeValue(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const labels = value.map(listItemLabel);
  if (labels.some((label) => label === null)) return null;
  return labels.filter((label): label is string => Boolean(label));
}

function historyChange(row: AuditEventRow): DocumentHistoryChange | undefined {
  if (row.event_type === "document.renamed") {
    const before = scalarChangeValue(row.previous_value);
    const after = scalarChangeValue(row.new_value);
    return before && after ? { field: "filename", before, after } : undefined;
  }

  const field = row.field_name ? EDIT_FIELDS[row.field_name] : undefined;
  if (!field) return undefined;
  const before = listChangeValue(row.previous_value);
  const after = listChangeValue(row.new_value);
  return before && after ? { field, before, after } : undefined;
}

/**
 * @param scopedDocuments every document in scope, or null when there were too
 * many to inline into the query. Given the index the query is narrowed to those
 * ids server-side; without it the events are filtered after the fact.
 */
export async function fetchAuditEvents(
  scope: DocumentHistoryScope,
  cursor: DocumentHistorySourceCursor,
  scopedDocuments: ScopedDocumentIndex | null
): Promise<DocumentHistorySourceCursor> {
  // Nothing in scope, so nothing in scope can have been deleted or restored.
  if (scopedDocuments?.size === 0) return exhausted(cursor);

  let query = supabase
    .from("event_logs")
    .select(
      "id, entity_id, event_type, field_name, previous_value, new_value, created_at, user_id, actor:user_profiles!event_logs_user_id_fkey(id, first_name, last_name, email, avatar_url, color)"
    )
    .eq("org_id", scope.orgId)
    .eq("entity_type", "document")
    .in("event_type", AUDIT_EVENT_TYPES)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .range(cursor.offset, cursor.offset + DOCUMENT_HISTORY_SOURCE_BATCH_SIZE - 1);

  if (scopedDocuments) {
    // Scope the events themselves. Left org-wide this walks every document
    // delete/restore in the organisation 50 rows at a time and throws away the
    // ones belonging to other buildings, so in a busy org this building's
    // events sit thousands of rows deep and every click spends the whole fetch
    // budget to return an empty page.
    //
    // Deliberately by resolved document id and NOT by `metadata->>buildingId`:
    // the writers build that key from a row that may be missing from the store
    // (`doc?.building_id`), and rows written before the key existed have no
    // metadata at all — an equality filter silently drops both.
    query = query.in("entity_id", [...scopedDocuments.keys()].map(String));
  }

  if (cursor.snapshotAt !== null) {
    query = query.lte("created_at", cursor.snapshotAt);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = (data as unknown as AuditEventRow[]) ?? [];
  const numericIds = rows
    .map((row) => Number.parseInt(row.entity_id, 10))
    .filter((id) => Number.isFinite(id));
  // The index already answers this for every row the filtered query returned.
  const documentsById = scopedDocuments ?? (await fetchScopedDocuments(scope, numericIds));

  const entries = rows.flatMap((row): DocumentHistoryEntry[] => {
    const documentId = Number.parseInt(row.entity_id, 10);
    const document = documentsById.get(documentId);
    // Out of scope, or permanently deleted. Both drop, deliberately: the event
    // row carries only `metadata.filename` and a `buildingId` its writers build
    // from `doc?.building_id` (absent on older rows), so an unresolvable event
    // cannot be attributed to this building — let alone to a project, which the
    // metadata never records. Falling back to it would surface other projects'
    // events in a project-scoped history and re-show the purged DWG children
    // this source is filtered to exclude. A purged document loses its "Added"
    // entry too (the created source reads `documents` as well), so history is
    // consistent: it vanishes whole rather than half. The durable record is
    // `event_logs` plus `deleted_documents_archive`, neither readable by
    // `authenticated` — surfacing it here needs an RPC, not a metadata guess.
    if (!document) return [];

    const action = historyAction(row.event_type);
    const change = historyChange(row);
    // An edit/rename label without its actual change is not useful history.
    // Legacy or malformed audit rows stay hidden instead of creating a filter
    // option and timeline item that cannot tell the user what happened.
    if ((action === "edited" || action === "renamed") && !change) return [];

    return [
      {
        id: `audit-${row.id}`,
        documentId: row.entity_id,
        displayName: null,
        filename: document.filename,
        filePath: document.file_path,
        contentType: document.content_type,
        size: document.size,
        version: document.version,
        versionGroup: document.version_group,
        isDeleted: document.is_deleted === true,
        state: document.state,
        action,
        change,
        createdAt: row.created_at,
        actor: actorFromProfile(row.actor, row.user_id),
      },
    ];
  });

  return {
    offset: cursor.offset + rows.length,
    done: rows.length < DOCUMENT_HISTORY_SOURCE_BATCH_SIZE,
    snapshotAt: resolveSnapshotAt(cursor, rows),
    // From the raw rows: a batch whose events all belonged to other buildings
    // yielded no entries, but it still read the timeline back this far, and that
    // is what unblocks the other source while this one is starved.
    watermark: resolveWatermark(cursor, rows),
    buffered: entries,
  };
}
